import React, { useContext, useState } from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { EventsFilterContext, EventsFilterDispatchContext } from 'context/eventsFilter';
import { LeavesContext } from 'context/leaves';

import { filterLeavesByDeviceType, getRootLeafById, groupByLeafType } from 'modules/leaves';

import { AppDeviceLeaf, AppGroupLeaf } from 'types/common/leaf';

import styles from './cameraFilter.module.scss';

export const CameraFilter = () => {
  const { leafStore } = useContext(LeavesContext);
  const leaves = leafStore.read();

  const { filterState } = useContext(EventsFilterContext);
  const dispatch = useContext(EventsFilterDispatchContext);

  const cameraLeaves = filterLeavesByDeviceType(leaves || {}, ['camera', 'doorbell']);

  // Separate groups and individual devices
  const { group: groups = {}, device: devices = {} } = groupByLeafType(cameraLeaves);

  const sortedGroups = Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  const sortedDevices = Object.values(devices).sort((a, b) => a.name.localeCompare(b.name));

  const hasSelectedChildren = (group: AppGroupLeaf): boolean =>
    !!group.memberLeaves &&
    Object.keys(group.memberLeaves).some((deviceId) => filterState.leaves[deviceId]?.value === true);

  // Track expanded state for each group
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    sortedGroups.reduce((acc, group) => ({ ...acc, [group.id]: hasSelectedChildren(group) }), {}),
  );

  const handleGroupChange = (group: AppGroupLeaf, checked: boolean): void => {
    const newCheckedItems = { ...filterState.leaves };

    // Set group checkbox state
    newCheckedItems[group.id] = { id: group.id, type: group.type, value: checked };

    // Set all child devices to the same state
    if (group.memberLeaves) {
      Object.values(group.memberLeaves).forEach((device) => {
        newCheckedItems[device.id] = { id: device.id, type: device.type, value: checked };
      });
    }

    dispatch({ type: 'filterState', payload: { leaves: newCheckedItems } });
  };

  const isGroupIndeterminate = (group: AppGroupLeaf): boolean => {
    const childDevices = Object.keys(group.memberLeaves);
    const checkedChildren = childDevices.filter((id) => filterState.leaves[id]?.value);

    return checkedChildren.length > 0 && checkedChildren.length < childDevices.length;
  };

  const isGroupChecked = (group: AppGroupLeaf): boolean => filterState.leaves[group.id]?.value === true;

  const handleAccordionToggle = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleDeviceChange = (device: AppDeviceLeaf, checked: boolean, parentGroupId?: string): void => {
    const newCheckedItems = { ...filterState.leaves };
    newCheckedItems[device.id] = { id: device.id, type: device.type, value: checked };

    const parentGroup = getRootLeafById<AppGroupLeaf>(groups, device.id);

    // If this device belongs to a group, update group state
    if (parentGroupId && parentGroup) {
      const childDevices = Object.values(parentGroup.memberLeaves || {});
      const checkedChildren = childDevices.filter((childDevice) =>
        childDevice.id === device.id ? checked : newCheckedItems[childDevice.id]?.value,
      );

      // Update group checkbox: checked if all children checked, unchecked if none, indeterminate if some
      if (checkedChildren.length === childDevices.length) {
        newCheckedItems[parentGroupId] = { id: parentGroupId, type: parentGroup.type, value: true };
      } else if (checkedChildren.length === 0) {
        newCheckedItems[parentGroupId] = { id: parentGroupId, type: parentGroup.type, value: false };
      } else {
        newCheckedItems[parentGroupId] = { id: parentGroupId, type: parentGroup.type, value: 'indeterminate' };
      }
    }

    dispatch({ type: 'filterState', payload: { leaves: newCheckedItems } });
  };

  return (
    <Box className={styles.container}>
      <FormGroup className={styles.formGroup}>
        {/* Render Individual Devices (not in groups) */}
        {sortedDevices?.map((device) => (
          <FormControlLabel
            key={device.id}
            control={
              <Checkbox
                checked={!!filterState.leaves[device.id]?.value}
                onChange={(e) => handleDeviceChange(device, e.target.checked)}
                data-testid={`checkbox-camera-${device.id}`}
              />
            }
            label={device.name}
          />
        ))}

        {/* Render Grouped Devices */}
        {sortedGroups?.map((group) => (
          <Accordion
            key={group.id}
            expanded={expandedGroups?.[group.id]}
            onChange={() => handleAccordionToggle(group.id)}
            className={styles.groupAccordion}
          >
            <AccordionSummary className={styles.groupHeader} expandIcon={<ExpandMore />}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isGroupChecked(group)}
                    indeterminate={isGroupIndeterminate(group)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleGroupChange(group, e.target.checked);
                    }}
                    data-testid={`checkbox-camera-group-${group.id}`}
                  />
                }
                label={
                  <Typography variant="subtitle1" fontWeight="bold">
                    {group.name}
                  </Typography>
                }
                onClick={(e) => e.stopPropagation()}
              />
            </AccordionSummary>
            <AccordionDetails className={styles.groupDetails}>
              {/* Child Devices */}
              {group.memberLeaves && (
                <FormGroup>
                  {Object.entries(group.memberLeaves).map(([deviceId, device]) => (
                    <FormControlLabel
                      key={deviceId}
                      control={
                        <Checkbox
                          checked={!!filterState.leaves[deviceId]?.value}
                          onChange={(e) => handleDeviceChange(device, e.target.checked, group.id)}
                          data-testid={`checkbox-camera-${deviceId}`}
                        />
                      }
                      label={device.name}
                    />
                  ))}
                </FormGroup>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </FormGroup>
    </Box>
  );
};
